import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { toast } from "sonner";
import { User, Phone, Mail, Save, ShieldCheck, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChangeEmailCard, ChangePasswordCard } from "@/components/profile/AccountSecurity";
import { PhoneField } from "@/components/PhoneField";
import { PhoneVerification } from "@/components/profile/PhoneVerification";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone_number || "");
  const [phoneCountry, setPhoneCountry] = useState(user?.phone_country || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", { name, phone_number: phone, phone_country: phoneCountry });
      setUser(data);
      if (data.claimed_reports > 0) {
        toast.success(`Profile updated — ${data.claimed_reports} past phone report${data.claimed_reports > 1 ? "s were" : " was"} attached to your account`);
      } else {
        toast.success("Profile updated");
      }
    } catch {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl w-full mx-auto px-6 py-12 animate-fade-up">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Account</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-8">Profile</h1>

        <div className="border border-border bg-[#0A0A0A] p-6 space-y-6">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground flex items-center gap-2 mb-2">
              <Mail className="w-3.5 h-3.5" /> Email
            </Label>
            <Input value={user?.email || ""} disabled data-testid="profile-email"
              className="bg-background border-border rounded-sm font-mono text-muted-foreground" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground flex items-center gap-2 mb-2">
              <User className="w-3.5 h-3.5" /> Full name
            </Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name"
              className="bg-background border-border rounded-sm focus-visible:ring-[#007AFF]" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground flex items-center gap-2 mb-2">
              <Phone className="w-3.5 h-3.5" /> Phone number (caller ID)
            </Label>
            <PhoneField value={phone} testidPrefix="profile-phone"
              onChange={(e164, dial) => { setPhone(e164); setPhoneCountry(dial); }} />
            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-[#007AFF]" />
              Voice reports from calls placed by this number are automatically filed to your account.
            </p>
            <div data-testid="profile-phone-required-note"
              className="mt-3 flex items-start gap-2 border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 rounded-sm">
              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-200/90 leading-relaxed">
                You must provide a valid phone number in your profile to create new records. Calls from
                a number that isn't saved here cannot be matched to your account and won't appear on your dashboard.
              </p>
            </div>
            <div className="mt-4">
              <PhoneVerification />
            </div>
          </div>
          <Button onClick={save} disabled={saving} data-testid="save-profile-btn"
            className="bg-[#007AFF] hover:bg-[#0066d6] text-white rounded-sm gap-2 font-semibold">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Profile"}
          </Button>
        </div>

        <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mt-10 mb-4">Security</p>
        <div className="space-y-6">
          <ChangeEmailCard />
          <ChangePasswordCard />
        </div>
      </div>
    </Layout>
  );
}
